import { useEffect } from 'react';

import {
  type ConnectionErrorForm,
  failConnectionAddOperation,
  failConnectionReconnectOperation,
  startConnectionAddOperation,
  startConnectionReconnectOperation,
  useConnectionApiErrorHandler,
} from '@mint-lib/connections-ui';
import type {
  AuthorizeResult,
  NormalizedConnection,
  OAuthFlow,
  PlatformUrn,
} from '@mint-lib/integration-registry';
import { OmniAuthAuthorize } from '@mint-sdk/api-omni';

/**
 * Ported from `packages/connections/src/components/_organisms/ManageConnectionForm/traffickingReconnectStrategies.ts`
 * sibling `defaultAuthorizeStrategy` (via `useOmniAuthAuthorize`) — the generic
 * OAuth authorize step shared by every non-quirky omni platform.
 */
export interface OmniAuthorizeParams {
  urn: PlatformUrn;
  connection?: NormalizedConnection;
  connectionName?: string;
  /** Receives field errors (duplicate name); without it every error is a snackbar. */
  form?: ConnectionErrorForm;
}

export function useOmniAuthorize({
  urn,
  connection,
  connectionName,
  form,
}: OmniAuthorizeParams): AuthorizeResult {
  const { mutate, data, isPending } = OmniAuthAuthorize.omniAuthAuthorizeV2();
  const handleApiError = useConnectionApiErrorHandler({ form });

  const redirectUrl = data?.data.items[0]?.redirectUrl;

  useEffect(() => {
    if (redirectUrl) {
      globalThis.location.assign(redirectUrl);
    }
  }, [redirectUrl]);

  return {
    authorize: () => {
      // Without a connection this is the add form; with one, a reconnect.
      const context = { platform: urn, flow: 'oauth', registry: true } as const;
      if (connection) {
        startConnectionReconnectOperation(context);
      } else {
        startConnectionAddOperation(context);
      }
      mutate(
        {
          data: {
            connectionId: connection?.id,
            connectionName: connectionName ?? connection?.name ?? '',
            platform: urn,
          },
        },
        {
          onError: (error) => {
            failConnectionAddOperation('error');
            failConnectionReconnectOperation('error');
            handleApiError(error);
          },
        },
      );
    },
    isPending,
  };
}

function canProcess(): boolean {
  // The generic omni OAuth round trip is handled entirely by the
  // `omniAuthAuthorize(V2)` mutation's redirect — there is no
  // `state`/`code` callback for this shell to distinguish, unlike
  // adform/cm/ga4.
  return false;
}

export function makeOmniOAuthFlow(urn: PlatformUrn): OAuthFlow {
  return {
    canProcess,
    useAuthorize: ({ connection }) => useOmniAuthorize({ urn, connection }),
  };
}
